import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  AdvancedDropdownProps,
  DropdownOption,
  DropdownSection,
} from './AdvancedDropdown.types'
import {
  DropdownContainer,
  DropdownTrigger,
  DropdownValue,
  DropdownIcon,
  DropdownContent,
  SearchContainer,
  SearchInput,
  DropdownList,
  Section,
  SectionHeader,
  DropdownSectionTitle,
  SectionCount,
  SectionToggle,
  SectionContent,
  Option,
  OptionIcon,
  OptionContent,
  OptionLabel,
  OptionDescription,
  EmptyState,
  LoadingState,
  ClearButton,
} from './AdvancedDropdown.styles'

export const AdvancedDropdown: React.FC<AdvancedDropdownProps> = ({
  value,
  onChange,
  options = [],
  sections = [],
  placeholder = 'Select...',
  disabled = false,
  loading = false,
  multiple = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  clearable = false,
  maxHeight = 300,
  width = '100%',
  open: controlledOpen,
  onOpenChange,
  filterFunction,
  renderOption,
  renderValue,
  className,
  error = false,
  size = 'medium',
  position = 'auto',
  showSectionCounts = false,
  emptyMessage = 'No options available',
  noResultsMessage = 'No results found',
  noHeader = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => {
      const initialCollapsed = new Set<string>()
      sections.forEach((section) => {
        if (section.defaultCollapsed) {
          initialCollapsed.add(section.id)
        }
      })
      return initialCollapsed
    }
  )
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>(
    'bottom'
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const open = controlledOpen !== undefined ? controlledOpen : isOpen

  // Handle position calculation
  useEffect(() => {
    if (open && position === 'auto' && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      if (spaceBelow < maxHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top')
      } else {
        setDropdownPosition('bottom')
      }
    } else if (position !== 'auto') {
      setDropdownPosition(position)
    }
  }, [open, position, maxHeight])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [open, searchable])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (disabled) return

      setIsOpen(newOpen)
      onOpenChange?.(newOpen)

      if (!newOpen) {
        setSearchTerm('')
      }
    },
    [disabled, onOpenChange]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleOpenChange(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, handleOpenChange])

  const defaultFilter = useCallback(
    (option: DropdownOption, term: string): boolean => {
      const searchText = term.toLowerCase()
      return !!(
        option.label.toLowerCase().includes(searchText) ||
        (option.description &&
          option.description.toLowerCase().includes(searchText))
      )
    },
    []
  )

  const filter = filterFunction || defaultFilter

  // Prepare all options (flat array from sections + standalone options)
  const allOptions = useMemo(() => {
    const sectionOptions = sections.flatMap((section) => section.options)
    return [...sectionOptions, ...options]
  }, [sections, options])

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allOptions
    return allOptions.filter((option) => filter(option, searchTerm))
  }, [allOptions, searchTerm, filter])

  // Organize filtered options by sections
  const organizedSections = useMemo(() => {
    if (!searchTerm && sections.length > 0) {
      return sections.map((section) => ({
        ...section,
        options: section.options.filter(
          (option) => !disabled || !option.disabled
        ),
      }))
    }

    // When searching, group filtered options by their original sections
    const filteredSections: DropdownSection[] = []
    const orphanOptions: DropdownOption[] = []

    filteredOptions.forEach((option) => {
      const originalSection = sections.find((s) =>
        s.options.some((o) => o.id === option.id)
      )
      if (originalSection) {
        let targetSection = filteredSections.find(
          (s) => s.id === originalSection.id
        )
        if (!targetSection) {
          targetSection = { ...originalSection, options: [] }
          filteredSections.push(targetSection)
        }
        targetSection.options.push(option)
      } else {
        orphanOptions.push(option)
      }
    })

    // Add standalone options as a separate section if any
    if (orphanOptions.length > 0) {
      filteredSections.push({
        id: '__standalone__',
        title: 'Options',
        options: orphanOptions,
      })
    }

    return filteredSections
  }, [sections, filteredOptions, searchTerm, disabled])

  const selectedOptions = useMemo(() => {
    if (!value) return []
    const values = Array.isArray(value) ? value : [value]
    return allOptions.filter((option) => values.includes(option.id))
  }, [value, allOptions])

  const handleOptionClick = useCallback(
    (option: DropdownOption) => {
      if (option.disabled) return

      if (multiple) {
        const currentValues = Array.isArray(value)
          ? value
          : value
            ? [value]
            : []
        const newValues = currentValues.includes(option.id)
          ? currentValues.filter((v) => v !== option.id)
          : [...currentValues, option.id]

        const newSelectedOptions = allOptions.filter((opt) =>
          newValues.includes(opt.id)
        )
        onChange?.(newValues, newSelectedOptions)
      } else {
        onChange?.(option.id, option)
        handleOpenChange(false)
      }
    },
    [multiple, value, allOptions, onChange, handleOpenChange]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange?.(multiple ? [] : '', multiple ? [] : undefined)
    },
    [multiple, onChange]
  )

  const handleSectionToggle = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }, [])

  const renderSelectedValue = useCallback(() => {
    if (selectedOptions.length === 0) {
      return (
        <DropdownValue className="placeholder">{placeholder}</DropdownValue>
      )
    }

    if (renderValue) {
      return (
        <DropdownValue>
          {renderValue(multiple ? selectedOptions : selectedOptions[0])}
        </DropdownValue>
      )
    }

    if (multiple) {
      if (selectedOptions.length === 1) {
        return <DropdownValue>{selectedOptions[0].label}</DropdownValue>
      }
      return (
        <DropdownValue>{selectedOptions.length} items selected</DropdownValue>
      )
    }

    return <DropdownValue>{selectedOptions[0].label}</DropdownValue>
  }, [selectedOptions, renderValue, multiple, placeholder])

  const renderOptionContent = useCallback(
    (option: DropdownOption) => {
      if (renderOption) {
        return renderOption(option)
      }

      return (
        <>
          {option.icon && <OptionIcon>{option.icon}</OptionIcon>}
          <OptionContent>
            <OptionLabel>{option.label}</OptionLabel>
            {option.description && (
              <OptionDescription>{option.description}</OptionDescription>
            )}
          </OptionContent>
        </>
      )
    },
    [renderOption]
  )

  const hasValue = selectedOptions.length > 0

  return (
    <DropdownContainer ref={containerRef} className={className}>
      <DropdownTrigger
        type="button"
        onClick={() => handleOpenChange(!open)}
        disabled={disabled}
        $disabled={disabled}
        $error={error}
        $open={open}
        $width={width}
        $size={size}
        $hasValue={hasValue}
      >
        {renderSelectedValue()}

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {clearable && hasValue && !disabled && (
            <ClearButton onClick={handleClear} title="Clear selection">
              ×
            </ClearButton>
          )}
          <DropdownIcon $open={open}>▼</DropdownIcon>
        </div>
      </DropdownTrigger>

      {open && (
        <DropdownContent
          ref={contentRef}
          $maxHeight={maxHeight}
          $position={dropdownPosition}
          $width={width}
        >
          {searchable && (
            <SearchContainer>
              <SearchInput
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </SearchContainer>
          )}

          <DropdownList>
            {loading ? (
              <LoadingState>Loading...</LoadingState>
            ) : organizedSections.length === 0 ? (
              <EmptyState>
                {searchTerm ? noResultsMessage : emptyMessage}
              </EmptyState>
            ) : (
              organizedSections.map((section) => {
                const isCollapsed = collapsedSections.has(section.id)
                const visibleOptions = section.options.filter(
                  (option) => !option.disabled || !disabled
                )

                if (visibleOptions.length === 0) return null

                return (
                  <Section key={section.id} $collapsed={isCollapsed}>
                    {!noHeader && (
                      <SectionHeader
                        $collapsible={!!section.collapsible}
                        onClick={
                          section.collapsible
                            ? () => handleSectionToggle(section.id)
                            : undefined
                        }
                      >
                        <DropdownSectionTitle>
                          {section.title}
                        </DropdownSectionTitle>
                        {showSectionCounts && (
                          <SectionCount>({visibleOptions.length})</SectionCount>
                        )}
                        {section.collapsible && (
                          <SectionToggle $collapsed={isCollapsed}>
                            ▼
                          </SectionToggle>
                        )}
                      </SectionHeader>
                    )}

                    <SectionContent $collapsed={isCollapsed}>
                      {visibleOptions.map((option) => {
                        const isSelected = selectedOptions.some(
                          (selected) => selected.id === option.id
                        )

                        return (
                          <Option
                            key={option.id}
                            onClick={() => handleOptionClick(option)}
                            $selected={isSelected}
                            $disabled={!!option.disabled}
                            $hasIcon={!!option.icon}
                            $hasDescription={!!option.description}
                          >
                            {renderOptionContent(option)}
                          </Option>
                        )
                      })}
                    </SectionContent>
                  </Section>
                )
              })
            )}
          </DropdownList>
        </DropdownContent>
      )}
    </DropdownContainer>
  )
}

AdvancedDropdown.displayName = 'AdvancedDropdown'
